import React, { createContext, useContext, useState, useEffect } from 'react';
import { identifyUser, resetUser, capture } from '../analytics';
import { noteNewMember } from '../onboarding/tour';

const AuthContext = createContext(null);

const notifyUpdater = (token) => {
  if (typeof window !== 'undefined' && window.appUpdater) window.appUpdater.setToken(token);
  // A running Job Agent session keeps syncing with the renewed token.
  if (typeof window !== 'undefined' && window.jobAgent?.sessionToken) window.jobAgent.sessionToken({ token });
  // So does a running Course Builder session (CONTRACTS.md: sessionToken(token)).
  if (typeof window !== 'undefined' && window.courseBuilder?.sessionToken) {
    Promise.resolve(window.courseBuilder.sessionToken(token)).catch(() => {});
  }
};

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // There is no lab quota to track any more: exactly one lab per vertical is
  // free, and the server says which by stamping `is_free` on each lab. Nothing
  // needs counting, so no progress fetch happens on sign-in.

  useEffect(() => {
    try {
      const token    = localStorage.getItem('lxops_token');
      const userData = localStorage.getItem('lxops_user');
      if (token && userData) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          notifyUpdater(token);
          identifyUser(token, parsedUser);
          // A shadow session is intentionally short — never renew it.
          // (the finally below still clears the loading flag)
          if (payload.shadow) return;
          import('../api').then((apiModule) => {
            // Refresh so tier changes (e.g. after payment) are picked up immediately
            apiModule.default.post('/auth/refresh')
              .then(({ data }) => {
                localStorage.setItem('lxops_token', data.token);
                notifyUpdater(data.token);
                if (data.user) {
                  localStorage.setItem('lxops_user', JSON.stringify(data.user));
                  setUser(data.user);
                  identifyUser(data.token, data.user);
                }
              })
              .catch(() => {
                // Refresh failed — carry on with the cached user.
              });
          });
        } else {
          localStorage.removeItem('lxops_token');
          localStorage.removeItem('lxops_user');
        }
      }
    } catch {
      localStorage.removeItem('lxops_token');
      localStorage.removeItem('lxops_user');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (token, rawUser) => {
    // A brand-new account queues the first-run tour; the flag is not kept on the user.
    const userData = noteNewMember(rawUser);
    localStorage.setItem('lxops_token', token);
    localStorage.setItem('lxops_user', JSON.stringify(userData));
    setUser(userData);
    notifyUpdater(token);
    identifyUser(token, userData);
    capture('user_signed_in', { tier: userData?.tier || 'free' });
  };

  const logout = () => {
    capture('user_signed_out');
    // Stops any campaign and forgets the token held by the desktop app.
    if (typeof window !== 'undefined' && window.jobAgent?.sessionEnd) window.jobAgent.sessionEnd();
    if (typeof window !== 'undefined' && window.courseBuilder?.sessionEnd) {
      Promise.resolve(window.courseBuilder.sessionEnd()).catch(() => {});
    }
    resetUser();
    localStorage.removeItem('lxops_token');
    localStorage.removeItem('lxops_user');
    setUser(null);
  };

  // A shadow session is an admin viewing the app as this member. Read straight
  // from the token rather than tracked in state, so it survives a reload and
  // cannot be cleared by anything but signing out.
  const shadowInfo = (() => {
    try {
      const t = localStorage.getItem('lxops_token');
      if (!t) return null;
      const c = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return c?.shadow ? { actor: c.act || 'an admin', expiresAt: c.exp * 1000 } : null;
    } catch { return null; }
  })();

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading,
      shadow:        shadowInfo,
      isPaid:        user?.tier === 'paid' || user?.tier === 'lifetime',
      isLifetime:    user?.tier === 'lifetime',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
